import React, { Component } from "react";
import axios from "axios";
import Header from "./Components/Header";
import SearchBusiness from "./Components/SearchBusiness";
import FetchBusiness from "./Components/FetchBusiness";
import BusinessData from "./Components/BusinessData";
import LimiterBadge from "./Components/Limiter/LimiterBadge";
import LimiterPopup from "./Components/Limiter/LimiterPopup";
import OverlayMessage from "./Components/OverlayMessage";
import { loadRecaptcha, captchaHeaders } from "./captcha";

import "./style.scss";

function trackFreeToolEvent(eventName, errorLabel) {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: eventName,
		eventCategory: 'www_free_tools',
		eventAction: 'id_generator',
		eventLabel: errorLabel || null,
		eventDescription: null,
	});
}

// Create a reference to the App instance
let AppInstance = null;

// Login status is now read from window.BL_LOGGED_IN (set by loginCheck.js)

// Functional component to handle animation


class App extends Component {
	state = {
		selectedBusiness: null,
		data: null,
		loading: false,
		limit: {},
		isLogged: false,
		popupOpened: false,
		errorMessage: "",
		errorDefaultMessage: 'This tool is currently unavailable due to scheduled maintenance - please try again later',
		backendUrl: ((window.wpApiSettings && window.wpApiSettings.pluginUrl) || '/wp-content/plugins/wp-id-generator/') + 'backend/public',
	};

	async componentDidMount() {
		AppInstance = this;
		// Warm up reCAPTCHA v3 (no-op when not configured) so the first search isn't delayed.
		loadRecaptcha().catch(() => {});
		await this.fetchLoggedInStatus();
		// Load initial limit state from bl-simple-limiter
		await this.limitUpdater();
	}

	componentWillUnmount() {
		// Clean up the global App instance
		AppInstance = null;
	}

	updateUsage = async () => {
		try {
			const { isLogged } = this.state;

			// Use bl-simple-limiter REST API endpoint
			const restBase = (window.wpApiSettings && window.wpApiSettings.restUrl) ? window.wpApiSettings.restUrl.replace('id-generator/v1/', '') : '/wp-json/';
			const updateUsageUrl = `${restBase}wp-api-limiter/v1/update-usage?logged_in=${isLogged}`;

			const { data } = await axios.post(updateUsageUrl);

			if (data) {
				const { used } = data.limit;
				// Convert to integer to ensure proper comparison
				this.setState((prevState) => ({
					limit: {
						...prevState.limit,
						used: parseInt(used, 10),
					},
				}));
			} else {
				console.error('Update usage error');
			}
		} catch (e) {
			console.error('Failed to update usage');
			if (e.response && e.response.data) {
				this.setState({ errorMessage: e.response.data.error });
			}
		}
	};

	limitUpdater = async () => {
		try {
			const { isLogged } = this.state;

			// Use bl-simple-limiter REST API endpoint
			const restBase = (window.wpApiSettings && window.wpApiSettings.restUrl) ? window.wpApiSettings.restUrl.replace('id-generator/v1/', '') : '/wp-json/';
			const limitApiUrl = `${restBase}wp-api-limiter/v1/endpoint?logged_in=${isLogged}`;

			const { data } = await axios.get(limitApiUrl);

			if (data) {
				const { limit } = data;
				// Convert string values to integers to ensure proper comparison
				const normalizedLimit = {
					used: parseInt(limit.used, 10),
					allowed: parseInt(limit.allowed, 10)
				};
				this.setState({ limit: normalizedLimit });

				if (this.checkLimitReached()) {
					this.openPopup();
				}
			} else {
				console.error('Limit not working here');
			}
		} catch (e) {
			console.error('Failed to connect to the free tools database');
			if (e.response && e.response.data) {
				this.setState({ errorMessage: e.response.data.error });
			}
		}
	};

	// Read login state from window.BL_LOGGED_IN (set by loginCheck.js)
	// Returns a Promise that resolves only AFTER setState has flushed,
	// so subsequent calls to limitUpdater() see the correct isLogged value.
	fetchLoggedInStatus = () => {
		return new Promise((resolve) => {
			let attempts = 0;
			const maxAttempts = 30; // 3 seconds max

			const checkLogin = setInterval(() => {
				attempts++;
				if (typeof window.BL_LOGGED_IN !== 'undefined') {
					clearInterval(checkLogin);
					console.log('[IDGenerator] BL_LOGGED_IN:', window.BL_LOGGED_IN);
					this.setState({ isLogged: window.BL_LOGGED_IN }, () => {
						console.log('[IDGenerator] isLogged state set to:', this.state.isLogged);
						resolve();
					});
				} else if (attempts >= maxAttempts) {
					clearInterval(checkLogin);
					console.log('[IDGenerator] BL_LOGGED_IN not found after 3s, defaulting to false');
					this.setState({ isLogged: false }, () => resolve());
				}
			}, 100);
		});
	}

	clear = () => {
		this.setState({ selectedBusiness: null, data: null });
		window.scrollTo(0, 0);
	};

	fetchPlacesV2Details = async (placeId) => {
		try {
			// Place Details are fetched server-side so the Google API key never reaches the browser.
			// The backend returns data already transformed to the legacy shape (including photo URLs).
			const url = `${this.state.backendUrl}/places-api-v2.php?place_id=${encodeURIComponent(placeId)}`;
			const response = await fetch(url, { credentials: 'same-origin', headers: await captchaHeaders() });

			if (!response.ok) {
				throw new Error(`Places API error: ${response.status}`);
			}

			return await response.json();
		} catch (error) {
			console.error('Places API v2 error:', error);
			return null; // Return null if Places API fails, we'll still have BrightLocal data
		}
	};

	fetch = async () => {
		const { selectedBusiness } = this.state;

		if (this.checkLimitReached()) {
			this.openPopup();
			return;
		}

		if (selectedBusiness) {
			const { place_id } = selectedBusiness;

			if (place_id) {
				this.setState({ loading: true });
				trackFreeToolEvent('details_submitted');

				// Check if Places API v2 is enabled
				const usePlacesV2 = true; // You can make this a setting if needed

				try {
					// Make parallel API calls
					const { isLogged } = this.state;
					const brightLocalUrl = `${this.state.backendUrl}/index.php?place_id=${place_id}&logged_in=${isLogged}`;

					let brightLocalData = null;

					// Just fetch BrightLocal data - we already have Places v2 data in selectedBusiness
					const brightLocalResponse = await axios.get(brightLocalUrl, { headers: await captchaHeaders() });
					brightLocalData = brightLocalResponse.data;

					if (brightLocalData && brightLocalData.success && brightLocalData.result && brightLocalData.result.ids && brightLocalData.result.links) {
						// Merge BrightLocal data with selectedBusiness (which already has Places v2 data)
						const mergedResult = {
							...selectedBusiness, // Already has Places v2 data from selectBusiness
							...brightLocalData.result // Add BrightLocal IDs and links
						};

						this.setState({ data: mergedResult });

						// Update usage count via bl-simple-limiter REST API
						// Frontend has access to the unique_id cookie required by bl-simple-limiter
						await this.updateUsage();
						this.setState({ loading: false });
						trackFreeToolEvent('details_submitted_successfully');
					} else {
						const errorMessage = (brightLocalData && brightLocalData.error) || "Unable to retrieve business details. This business may not yet be indexed in BrightLocal, or there may be an issue with the data source.";
						this.setState({
							errorMessage,
							loading: false
						});
						trackFreeToolEvent('tool_error', errorMessage);
					}
				} catch (e) {
					console.error('Fetch error:', e);
					// Check for specific errors
					if (e.response && e.response.data) {
						if (e.response.data.limitReached) {
							this.openPopup();
							this.clear();
							trackFreeToolEvent('tool_error', 'Limit reached');
						} else {
							const errorMessage = e.response.data.error || "An unexpected error occurred. Please try again later.";
							this.setState({ errorMessage });
							trackFreeToolEvent('tool_error', errorMessage);
						}
					} else {
						const errorMessage = "Unexpected error: Unable to process your request at this time.";
						this.setState({ errorMessage });
						trackFreeToolEvent('tool_error', errorMessage);
					}
					this.setState({ loading: false });
				}

			} else {
				this.setState({ loading: false });
			}
		}
	};

	selectBusiness = async (business) => {
		// Set the business immediately so UI shows it
		this.setState({ selectedBusiness: business });

		// FindLocation already fetched full details (rating, hours, photos) via the
		// backend when the suggestion was picked. Only fetch again if they're missing.
		if (business.place_id && business.rating === undefined && !business.photos) {
			try {
				const placesV2Data = await this.fetchPlacesV2Details(business.place_id);
				if (placesV2Data) {
					// Merge Places v2 data into selectedBusiness
					this.setState((prevState) => ({
						selectedBusiness: {
							...prevState.selectedBusiness,
							...placesV2Data
						}
					}));
				}
			} catch (error) {
				console.error('Error fetching Places v2 details:', error);
				// Still show the business even if Places API fails
			}
		}
	};

	openPopup = () => {
		this.setState({ popupOpened: true });
		setTimeout(() => {
			let popup = document.querySelector('.bg-popup');
			if (popup) {
				popup.style.display = 'flex';
			}
		}, 300);
	};

	goToId = () => {
		this.fetch();
	};

	closePopup = () => {
		this.setState({ popupOpened: false });
		setTimeout(() => {
			let popup = document.querySelector('.bg-popup');
			if (popup) {
				popup.style.display = 'none';
			}
		}, 400);
	};

	checkLimitReached = () => {
		let { limit, isLogged } = this.state;

		if (!limit) {
			return false;
		}

		if (!limit.hasOwnProperty('used') || !limit.hasOwnProperty('allowed')) {
			return false;
		}

		const limitReached = limit.used >= limit.allowed;
		return limitReached;
	};

	render() {
		const { loading, data, selectedBusiness, popupOpened, isLogged, limit, errorMessage } = this.state;

		return (
			<>
				<LimiterBadge
					isLogged={isLogged}
					limit={limit}
				/>
				
				<div className="idg-main">
					<Header data={data} />

					{!data ? (
						<>
							

							<LimiterPopup
								closePopup={this.closePopup}
								isOpened={popupOpened}
								isLogged={isLogged}
								limit={limit}
							/>

							
								<div className="idg-hero idg-animate-me">
									
									{errorMessage && <OverlayMessage message={errorMessage} className="idg-error-message" />}

									{!errorMessage && !loading ? (
										selectedBusiness ? (
											<FetchBusiness
												business={selectedBusiness}
												onFetch={this.goToId}
												onClear={this.clear}
											/>
										) : (
										
												<SearchBusiness
													onOpenPopup={this.openPopup}
													isSearchDisabled={this.checkLimitReached()}
													onSelect={this.selectBusiness}
												/>
										
										)
									) : null}

									{loading && !errorMessage ? (
										<div className="idg-loader-outer">
											<svg className="loader" xmlns="http://www.w3.org/2000/svg" version="1.2" baseProfile="tiny" x="0" y="0" viewBox="0 0 200 200" xmlSpace="preserve">
												<path className="loaderreverse" d="M200 100c0-30.3-13.5-57.5-34.8-75.8 -4.8-4.1-12.2-3-15.8 2.3v0c-3 4.5-2.4 10.7 1.8 14.2 16.6 14.4 27.1 35.6 27.1 59.3s-10.5 44.9-27.1 59.3c-4.1 3.6-4.8 9.7-1.8 14.2v0c3.6 5.3 11 6.4 15.8 2.3C186.5 157.5 200 130.3 200 100z" />
												<path d="M156.7 100c0-14.9-5.8-28.5-15.2-38.6 -4.6-4.9-12.6-4.1-16.3 1.4l-0.4 0.6c-2.8 4.1-2.2 9.5 1.2 13.2 5.7 6.2 9.1 14.4 9.1 23.5 0 9-3.4 17.3-9.1 23.5 -3.3 3.7-3.9 9-1.2 13.2l0.4 0.6c3.7 5.6 11.7 6.3 16.3 1.4C150.9 128.5 156.7 114.9 156.7 100z" />
											</svg>
										</div>
									) : null}
								</div>
							
						</>
					) : (
						<BusinessData data={data} onReset={this.clear} />
					)}
				</div>
			</>
		);
	}
}

export default App;
